import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Tooltip } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { StorePicker } from '../../components/StorePicker';
import { useAuthContext } from '../../context/AuthContext';
import { useNotificationContext } from '../../context/NotificationContext';
import { usePersistedState } from '../../hooks/usePersistedState';
import { ApiService } from '../../services/api';
import {
  ControlPogResetInstallMapPoint,
  ControlPogResetInstallMapResponse,
  Store,
} from '../../types';
import { isPlainLeftClick } from '../../utils/navigation';
import { clearTaskIdCache, readTaskIdCache, taskIdsByTag, writeTaskIdCache } from './taskIdCache';

// Deployment progress, least to most complete. A mod shows the furthest state it has
// reached, so an assigned mod stays green even though its aisle is also signed off.
// 'reviewed' is not a server status: it comes from the mod's reset task and outranks every
// other state, so a reviewed mod reads as purple whatever else is true of it.
const STATUS_STYLES: Record<MapStatus, string> = {
  not_installed: 'bg-gray-400 border-gray-500',
  installed: 'bg-orange-500 border-orange-600',
  signed_off: 'bg-blue-500 border-blue-600',
  assigned: 'bg-green-600 border-green-700',
  reviewed: 'bg-purple-600 border-purple-700',
  signed_off_no_task: 'bg-amber-800 border-amber-900',
};

const STATUS_LABELS: Record<MapStatus, string> = {
  not_installed: 'not installed (plotted position)',
  installed: 'camera installed, aisle not signed off',
  signed_off: 'aisle signed off, no POG assigned',
  assigned: 'POG assigned',
  reviewed: 'reviewed (queued for in-store reset)',
  signed_off_no_task: 'aisle signed off, no reset task to open',
};

const STATUS_SWATCHES = STATUS_STYLES;

// Camera health is not drawn on the dots — no ring color separated reliably from the orange
// status fill at this dot size. It reaches the user through the filter and the tooltip instead.
const HEALTH_LABELS: Record<ControlPogResetInstallMapPoint['cameraHealth'], string> = {
  ok: '',
  missed_schedules: 'MISSING SCHEDULED IMAGES',
  offline: 'CAMERA OFFLINE',
};

/**
 * Server statuses plus the two the client derives from the mod's reset task: 'reviewed'
 * (queued for reset) and 'signed_off_no_task' (signed off but with no task to open).
 */
type MapStatus = ControlPogResetInstallMapPoint['status'] | 'reviewed' | 'signed_off_no_task';

type OfflineFilter = 'all' | 'hide_offline' | 'only_offline';

const OFFLINE_FILTERS: { value: OfflineFilter; label: string }[] = [
  { value: 'all', label: 'All cameras' },
  { value: 'hide_offline', label: 'Hide offline' },
  { value: 'only_offline', label: 'Only offline' },
];

const isOfflineFilter = (value: unknown): value is OfflineFilter =>
  value === 'all' || value === 'hide_offline' || value === 'only_offline';

const isStatusList = (value: unknown): value is MapStatus[] =>
  Array.isArray(value) && value.every((entry) => entry in STATUS_STYLES);

const LEGEND: { status: MapStatus; label: string }[] = [
  { status: 'not_installed', label: 'Not installed (from map plot)' },
  { status: 'installed', label: 'Installed, not signed off' },
  { status: 'signed_off', label: 'Signed off' },
  { status: 'assigned', label: 'POG assigned' },
  { status: 'reviewed', label: 'Reviewed' },
];

const renderPointTooltip = (
  point: ControlPogResetInstallMapPoint & { displayStatus: MapStatus }
): React.ReactNode => {
  // An 'assigned' mod is usually signed off too, so the sign-off is its own line rather
  // than a trailing clause on the status label.
  const signOff = [
    point.signedOffBy ? `by ${point.signedOffBy}` : '',
    point.signedOffAt ? `on ${new Date(point.signedOffAt).toLocaleString()}` : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="text-xs leading-relaxed">
      <div className="font-semibold">{point.tagName}</div>
      {point.displayStatus === 'reviewed' && (
        <div className="font-semibold text-purple-300">Reviewed — queued for in-store reset</div>
      )}
      {/* Purple hides the deployment state, so state it explicitly for a reviewed mod. */}
      <div>{STATUS_LABELS[point.status]}</div>
      {signOff && <div className="text-gray-300">Signed off {signOff}</div>}
      {point.cameraHealth !== 'ok' && (
        <div className="mt-1 font-semibold text-amber-300">
          {HEALTH_LABELS[point.cameraHealth]}
          {point.cameraHealthDescription && (
            <span className="font-normal"> — {point.cameraHealthDescription}</span>
          )}
          {point.cameraHealthSince && (
            <div className="font-normal text-gray-300">
              since {new Date(point.cameraHealthSince).toLocaleString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Dots sit inside the pannable map, so a click that ends a drag must not navigate.
const PAN_SLOP_PX = 4;

/** Fan out dots sharing the exact same map position (mods covered by one camera). */
const spreadOverlaps = <T extends { x: number; y: number }>(
  points: T[]
): (T & { offsetIndex: number; offsetCount: number })[] => {
  const groups = new Map<string, T[]>();
  points.forEach((point) => {
    const key = `${point.x},${point.y}`;
    const group = groups.get(key) ?? [];
    group.push(point);
    groups.set(key, group);
  });
  return points.map((point) => {
    const group = groups.get(`${point.x},${point.y}`) ?? [point];
    return { ...point, offsetIndex: group.indexOf(point), offsetCount: group.length };
  });
};

export const PogResetInstallMapPage: React.FC = () => {
  const { storeName } = useParams<{ storeName: string }>();
  const { token } = useAuthContext();
  const { toggleLoading, showErrorToast } = useNotificationContext();
  const navigate = useNavigate();

  const [data, setData] = useState<ControlPogResetInstallMapResponse | null>(null);
  const [store, setStore] = useState<Store | null>(null);
  // The picker stays within the current store's chain — cross-chain jumps are not a workflow
  // this page serves.
  const [chainStores, setChainStores] = useState<Store[]>([]);
  const [mapSize, setMapSize] = useState<{ width: number; height: number } | null>(null);
  // Persisted as an array — a Set does not survive JSON round-tripping.
  const [hiddenStatusList, setHiddenStatusList] = usePersistedState<MapStatus[]>(
    'pogReset.installMap.hiddenStatuses',
    [],
    isStatusList
  );
  const [offlineFilter, setOfflineFilter] = usePersistedState<OfflineFilter>(
    'pogReset.installMap.offlineFilter',
    'all',
    isOfflineFilter
  );
  const [search, setSearch] = usePersistedState<string>(
    'pogReset.installMap.search',
    '',
    (value): value is string => typeof value === 'string'
  );
  const hiddenStatuses = useMemo(() => new Set(hiddenStatusList), [hiddenStatusList]);
  // The install-map endpoint carries no task id, so mod rows supply it for the dot links.
  // Only tag -> task id is kept, which is all the links need and small enough to cache.
  const [taskIdByTag, setTaskIdByTag] = useState<Record<string, number>>({});
  // Never cached: the reviewed marker is toggled as people work through the list, so a stale
  // copy would mis-colour dots. Task ids are stable, this is not.
  const [reviewedTaskIds, setReviewedTaskIds] = useState<Set<number>>(new Set());
  const dragOriginRef = useRef<{ x: number; y: number } | null>(null);

  const toggleStatus = (status: MapStatus) => {
    setHiddenStatusList((prev) =>
      prev.includes(status) ? prev.filter((value) => value !== status) : [...prev, status]
    );
  };

  const refreshTaskIds = async () => {
    if (!storeName) {
      return;
    }

    clearTaskIdCache(storeName);
    try {
      const mods = await ApiService.fetchPogResetMods(token, storeName);
      const fresh = taskIdsByTag(mods.mods);
      setTaskIdByTag(fresh);
      writeTaskIdCache(storeName, token, fresh);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Error refreshing task links');
    }
  };

  useEffect(() => {
    if (!storeName) return undefined;
    let active = true;
    const load = async () => {
      toggleLoading(true);
      setData(null);
      setMapSize(null);
      try {
        // Dot links only need tag -> task id, so a cached copy skips the mods request
        // entirely; the map itself is always fetched fresh.
        const cachedTaskIds = readTaskIdCache(storeName, token);
        const [response, mods, tasks, stores] = await Promise.all([
          ApiService.fetchPogResetInstallMap(token, storeName),
          // Task ids are a nice-to-have for linking; a failure here must not blank the map.
          cachedTaskIds ? null : ApiService.fetchPogResetMods(token, storeName).catch(() => null),
          // Open tasks only — a closed task's reset is already done, so its mods should show
          // their real deployment status rather than staying purple forever.
          ApiService.fetchPogResetTasks(token, storeName, 'open').catch(() => null),
          // Only for the header label — the map must still render if it fails.
          ApiService.fetchStores(token).catch(() => null),
        ]);
        if (!active) return;
        setData(response);
        const currentStore = stores?.find((entry) => entry.storeName === storeName) ?? null;
        setStore(currentStore);
        setChainStores(
          currentStore ? (stores ?? []).filter((s) => s.chainId === currentStore.chainId) : []
        );
        setReviewedTaskIds(
          new Set((tasks?.tasks ?? []).filter((task) => task.reviewed).map((task) => task.taskId))
        );
        if (cachedTaskIds) {
          setTaskIdByTag(cachedTaskIds);
        } else if (mods) {
          const fresh = taskIdsByTag(mods.mods);
          setTaskIdByTag(fresh);
          writeTaskIdCache(storeName, token, fresh);
        }
      } catch (err) {
        if (!active) return;
        showErrorToast(err instanceof Error ? err.message : 'Error loading install map');
      } finally {
        if (active) toggleLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, storeName]);

  const taskIdByPoint = useMemo(
    () =>
      (point: ControlPogResetInstallMapPoint): number | null =>
        taskIdByTag[point.tagName] ?? null,
    [taskIdByTag]
  );

  // 'reviewed' outranks the server status, so resolve it once and let filtering, counts and
  // dot colour all read the same value.
  const allPoints = useMemo(
    () =>
      (data?.points ?? []).map((point) => {
        const taskId = taskIdByPoint(point);
        const isReviewed = taskId != null && reviewedTaskIds.has(taskId);
        // A signed-off mod with no reset task cannot be opened, so it gets its own colour
        // rather than looking like the clickable blue ones.
        const displayStatus: MapStatus = isReviewed
          ? 'reviewed'
          : point.status === 'signed_off' && taskId == null
            ? 'signed_off_no_task'
            : point.status;
        return { ...point, displayStatus };
      }),
    [data, taskIdByPoint, reviewedTaskIds]
  );

  const points = useMemo(() => {
    const query = search.trim().toLowerCase();
    return spreadOverlaps(
      allPoints
        // Brown dots have no pill of their own, so the signed-off pill hides them too.
        .filter((point) =>
          point.displayStatus === 'signed_off_no_task'
            ? !hiddenStatuses.has('signed_off')
            : !hiddenStatuses.has(point.displayStatus)
        )
        .filter((point) => {
          if (offlineFilter === 'hide_offline') {
            return point.cameraHealth !== 'offline';
          }

          if (offlineFilter === 'only_offline') {
            return point.cameraHealth === 'offline';
          }

          return true;
        })
        .filter((point) => !query || point.tagName.toLowerCase().includes(query))
    );
  }, [allPoints, hiddenStatuses, offlineFilter, search]);

  // Carried into the task page so its prev/next walks the dots currently on screen.
  const visibleTaskIds = useMemo(
    () => [...new Set(points.map(taskIdByPoint).filter((id): id is number => id != null))],
    [points, taskIdByPoint]
  );

  // Counted on the display status, so a reviewed mod leaves the signed-off/assigned tallies
  // rather than being counted twice. 'signed_off_no_task' has no pill of its own — it is a
  // shade of signed off, so it counts there and toggles with it.
  const statusCounts = useMemo(() => {
    const counts: Record<MapStatus, number> = {
      not_installed: 0,
      installed: 0,
      signed_off: 0,
      assigned: 0,
      reviewed: 0,
      signed_off_no_task: 0,
    };
    allPoints.forEach((point) => {
      counts[point.displayStatus] += 1;
    });
    counts.signed_off += counts.signed_off_no_task;

    return counts;
  }, [allPoints]);

  if (!data) {
    return <div className="min-h-screen bg-gray-100 p-6" />;
  }

  // Bound outside the TransformWrapper render prop, whose callback drops the null narrowing.
  // Only rendered in the branch where mapUrl is non-null.
  const mapUrl = data.mapUrl ?? undefined;

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="w-4/5 mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <Link
            to={`/stores/${storeName}/pog-reset`}
            className="p-1 text-blue-600 hover:bg-blue-50 rounded-md"
            title="Back to reset tasks"
          >
            <ArrowBackIcon />
          </Link>
          <div>
            <h1 className="text-2xl font-bold mb-0.5">
              Installation progress
              <span className="ml-2 text-base font-normal text-gray-600">
                {store?.legibleName ?? storeName}
              </span>
            </h1>
            <p className="text-sm text-gray-600">
              {data.installedMods}/{data.totalMods} mods installed · {data.signedOffMods} signed off
              · {data.assignedMods} with a POG assigned
              {data.offlineCameraMods > 0 && (
                <span className="text-red-700 font-medium">
                  {' '}
                  · {data.offlineCameraMods} camera{data.offlineCameraMods === 1 ? '' : 's'} offline
                </span>
              )}
              {data.missedScheduleCameraMods > 0 && (
                <span className="text-amber-700 font-medium">
                  {' '}
                  · {data.missedScheduleCameraMods} missing images
                </span>
              )}
            </p>
          </div>
          <div className="ml-auto">
            <StorePicker
              stores={chainStores}
              current={store}
              onSelect={(s) => navigate(`/stores/${s.storeName}/pog-reset/install-map`)}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs mb-3">
          {LEGEND.map(({ status, label }) => {
            const hidden = hiddenStatuses.has(status);
            return (
              <button
                key={status}
                type="button"
                onClick={() => toggleStatus(status)}
                aria-pressed={!hidden}
                title={hidden ? `Show ${label}` : `Hide ${label}`}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-full border transition-colors ${
                  hidden
                    ? 'border-gray-200 bg-gray-100 text-gray-400'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span
                  className={`w-2.5 h-2.5 rounded-full border ${
                    hidden ? 'bg-transparent border-gray-400' : STATUS_SWATCHES[status]
                  }`}
                />
                <span className={hidden ? 'line-through' : undefined}>{label}</span>
                <span className="text-gray-400">{statusCounts[status]}</span>
              </button>
            );
          })}

          {data.offlineCameraMods > 0 && (
            <div className="flex items-center rounded-full border border-gray-300 overflow-hidden ml-1">
              {OFFLINE_FILTERS.map(({ value, label }) => {
                const active = offlineFilter === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setOfflineFilter(value)}
                    aria-pressed={active}
                    className={`px-2 py-1 border-l border-gray-300 first:border-l-0 transition-colors ${
                      active ? 'bg-gray-700 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {label}
                    {value === 'only_offline' && (
                      <span className={active ? 'text-gray-300 ml-1' : 'text-gray-400 ml-1'}>
                        {data.offlineCameraMods}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Filter by mod tag, e.g. GR-14-L-5"
            className="px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-56 ml-1 uppercase placeholder:normal-case"
          />
          {search.trim() && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="text-blue-600 hover:underline"
            >
              Clear
            </button>
          )}
          <button
            type="button"
            onClick={refreshTaskIds}
            className="text-gray-500 hover:text-gray-800 hover:underline ml-auto"
            title="Task links are cached for a couple of minutes — refresh if a dot opens a closed task"
          >
            Refresh task links
          </button>
        </div>

        {data.missedScheduleCameraMods > 0 && (
          <div className="text-xs text-gray-500 mb-3">
            {data.missedScheduleCameraMods} mod{data.missedScheduleCameraMods === 1 ? '' : 's'} with
            an open <span className="font-mono">missed_schedules</span> task (under half arriving) —
            hover a dot to see camera health.
          </div>
        )}

        {data.points.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
            No mod map plots or camera locations for this store yet.
          </div>
        ) : !data.mapUrl ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
            No store map uploaded — dots cannot be placed without the map image.
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md p-4 overflow-auto">
            {points.length === 0 && (
              <div className="text-center text-sm text-gray-500 mb-3">
                No mods match the current filters.
              </div>
            )}
            <TransformWrapper
              wheel={{ step: 0.2 }}
              doubleClick={{ mode: 'reset' }}
              pinch={{ step: 5 }}
              limitToBounds={true}
              centerOnInit={true}
            >
              {({ zoomIn, zoomOut, resetTransform }) => (
                <>
                  <div className="flex items-center gap-2 mb-2 text-xs">
                    <button
                      type="button"
                      onClick={() => zoomIn()}
                      className="px-2 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    >
                      Zoom in
                    </button>
                    <button
                      type="button"
                      onClick={() => zoomOut()}
                      className="px-2 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    >
                      Zoom out
                    </button>
                    <button
                      type="button"
                      onClick={() => resetTransform()}
                      className="px-2 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    >
                      Reset
                    </button>
                    <span className="text-gray-400">
                      Scroll to zoom · drag to pan · double-click to reset
                    </span>
                  </div>
                  <TransformComponent
                    wrapperStyle={{ width: '100%' }}
                    contentStyle={{ width: '100%' }}
                  >
                    <div className="relative w-full">
                      <img
                        src={mapUrl}
                        alt={`${storeName} store map`}
                        className="w-full h-auto select-none"
                        onLoad={(event) =>
                          setMapSize({
                            width: event.currentTarget.naturalWidth,
                            height: event.currentTarget.naturalHeight,
                          })
                        }
                      />
                      {mapSize &&
                        points.map((point) => {
                          const taskId = taskIdByPoint(point);
                          const dotClassName = `absolute w-2.5 h-2.5 -mt-[5px] rounded-full border flex items-center justify-center text-[6px] leading-none font-bold text-white no-underline hover:z-20 hover:scale-[2.2] transition-transform ${
                            taskId != null ? 'cursor-pointer' : 'cursor-default'
                          } ${STATUS_STYLES[point.displayStatus]}`;
                          const dotStyle = {
                            left: `${(point.x / mapSize.width) * 100}%`,
                            top: `${(point.y / mapSize.height) * 100}%`,
                            // Spacing exceeds the dot's footprint (10px plus its offset ring)
                            // so fanned-out dots stay separate instead of merging into a blob.
                            marginLeft: (point.offsetIndex - (point.offsetCount - 1) / 2) * 14 - 5,
                          };

                          return (
                            <Tooltip
                              key={`${point.tagName}-${point.modCoverageId ?? 'plot'}`}
                              title={renderPointTooltip(point)}
                              placement="top"
                              arrow
                            >
                              {taskId == null ? (
                                <div className={dotClassName} style={dotStyle}>
                                  {point.modNumber ?? ''}
                                </div>
                              ) : (
                                // A real href so middle-click and ctrl+click open a new tab
                                // natively; only a plain left click routes in-page. ?from=
                                // stands in for the router state a new tab cannot inherit.
                                <a
                                  href={`/stores/${storeName}/pog-reset/${taskId}?from=install-map`}
                                  onPointerDown={(event) => {
                                    dragOriginRef.current = { x: event.clientX, y: event.clientY };
                                  }}
                                  onClick={(e) => {
                                    if (!isPlainLeftClick(e)) {
                                      return;
                                    }

                                    e.preventDefault();
                                    // Panning ends in a click on the dot, so a click that
                                    // travelled is a drag, not a selection.
                                    const origin = dragOriginRef.current;
                                    if (
                                      origin != null &&
                                      (Math.abs(e.clientX - origin.x) > PAN_SLOP_PX ||
                                        Math.abs(e.clientY - origin.y) > PAN_SLOP_PX)
                                    ) {
                                      return;
                                    }

                                    navigate(`/stores/${storeName}/pog-reset/${taskId}`, {
                                      state: {
                                        taskIds: visibleTaskIds,
                                        backTo: `/stores/${storeName}/pog-reset/install-map`,
                                      },
                                    });
                                  }}
                                  className={dotClassName}
                                  style={dotStyle}
                                >
                                  {point.modNumber ?? ''}
                                </a>
                              )}
                            </Tooltip>
                          );
                        })}
                    </div>
                  </TransformComponent>
                </>
              )}
            </TransformWrapper>
          </div>
        )}
      </div>
    </div>
  );
};
